import React, {useMemo} from 'react'
import Plate from "../../Plate";
import DotsIcon from "../../../assets/icon/DotsIcon";
import DropDownPicker from "../../DropDownPicker";
import {
    FIXED_PROJECT_STATUS_COLORS,
    getProjectStatusColor,
} from "../../../utils/projectStatus";

import styles from './index.module.scss'
import _default from "chart.js/dist/plugins/plugin.tooltip";
import backgroundColor = _default.defaults.backgroundColor;
import {useTaskApprove} from "../../../context/taskApprove.context";

interface TaskStatusOption {
    key?: string
    id?: string | number
    title?: string
    name?: string
    color?: string
}

const fallbackTaskStatusPickerList = [
    {
        id: "backlog",
        title: "Backlog/Ideas",
        color: FIXED_PROJECT_STATUS_COLORS.backlog,
        icon: <DotsIcon color={FIXED_PROJECT_STATUS_COLORS.backlog}/>,
    },
    {
        id: "in_progress",
        title: "In progress",
        color: FIXED_PROJECT_STATUS_COLORS.in_progress,
        icon: <DotsIcon color={FIXED_PROJECT_STATUS_COLORS.in_progress}/>,
    },
    {
        id: "blocked",
        title: "Blocked",
        color: "#DC2626",
        icon: <DotsIcon color={"#DC2626"}/>,
    },
    {
        id: "complete",
        title: "Completed",
        color: FIXED_PROJECT_STATUS_COLORS.complete,
        icon: <DotsIcon color={FIXED_PROJECT_STATUS_COLORS.complete}/>,
    },
]

interface TaskStatusPickerProps {
    getStatus: (statusId: string | number) => void
    pickedId: string | number
    statuses?: TaskStatusOption[]
    taskData: any
    onlyIcon?: boolean
    dropdownPosition?: 'left' | 'right' | 'side-right'
}

export default function TaskStatusPicker({getStatus, taskData, pickedId, statuses, onlyIcon, dropdownPosition = 'left'}: TaskStatusPickerProps) {
    const {initiateCompleteTask} = useTaskApprove()
    const taskStatusPickerList = useMemo(() => {
        if (!statuses?.length) {
            return fallbackTaskStatusPickerList
        }

        return statuses.map((status) => {
            const id = status.key || status.id || ''
            const color = getProjectStatusColor(String(id), status.color || '#B1AEC7')

            return {
                id,
                title: status.title || status.name || String(id),
                color,
                icon: <DotsIcon color={color}/>,
            }
        })
    }, [statuses])

    const pickedStatus = useMemo(() => {
        return taskStatusPickerList.find(({id}) => pickedId === id)
    }, [pickedId, taskStatusPickerList]);

    const getValue = (status: string) => {
        if(status === 'complete'){
            initiateCompleteTask(taskData)
            return;
        }
        getStatus(status)
    }

    return (
        <div className={styles.taskStatusPicker}>
            <DropDownPicker
                closeDropDownByPick={true}
                pickerList={taskStatusPickerList}
                position={dropdownPosition}
                maxVisibleItems={4}
                dropdownClassName={styles.taskStatusPicker__dropdown}
                listClassName={styles.taskStatusPicker__list}
                itemClassName={styles.taskStatusPicker__item}
                btn={(isOpen: boolean) => onlyIcon ? (
                    <div>
                        {pickedStatus?.icon || <DotsIcon color={isOpen ? '#64617E' : '#B1AEC7'}/>}
                    </div>
                ) : (
                    <Plate title={pickedStatus?.title || "Status"}
                           icon={pickedStatus?.icon || <DotsIcon color={isOpen ? '#64617E' : '#B1AEC7'}/>}
                           active={isOpen}
                           filled={!!pickedStatus}
                           accentColor={pickedStatus?.color}
                    />
                )}
                getValue={getValue}/>
        </div>
    )
}
